import { forwardRef, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { analyzeResume, DEMO_RESUME } from "@/lib/resumeAnalyzer";
import { extractText, wordCount } from "@/lib/extractText";
import { funnelHref } from "@/lib/site";

const Finding = ({ f, open, onToggle }) => (
  <article className={`finding ${open ? "expanded" : ""}`} data-testid={`lab-finding-${f.id}`}>
    <button aria-expanded={open} onClick={onToggle}>
      <div><span className={`status ${f.priority}`}>{f.priority}</span><h4>{f.label}</h4></div>
      <strong>{f.score}<small>/100</small></strong>
    </button>
    <div className="meter"><i style={{ width: `${f.score}%` }} /></div>
    {open && <div className="findingBody">
      <p><b>Observed fact</b>{f.observed}</p><p><b>Why it matters</b>{f.impact}</p><p className="fix"><b>Most useful fix</b>{f.fix}</p>
    </div>}
  </article>
);

const Results = ({ res }) => (
  <div className={`resultsPanel ${res ? "shown" : "muted"}`} id="results" aria-live="polite" data-testid="lab-results">
    <div className="resultHeader">
      <div><span>WEIGHTED READINESS</span><strong data-testid="lab-score">{res ? res.score : "—"}</strong></div>
      <p>{res ? `${res.sectionsDetected}/5 core sections detected` : "Your analysis will appear here."}</p>
    </div>
    <h3>Most important fixes</h3>
    {res ? <>
      <ol className="priorityList">{res.priorities.map((p, i) => <li key={p.id}><span>{i + 1}</span><div><b>{p.label}</b><p>{p.fix}</p></div></li>)}</ol>
      <div className="resultNext">
        <p><b>Ready for the next step?</b>Use these findings yourself or develop a tailored resume.</p>
        <Link to="/resume-service" data-testid="lab-develop-btn">Develop my resume →</Link>
        <a href={funnelHref("/?diagnostic=1")} data-testid="lab-career-btn">Explore my AI career path</a>
      </div>
    </> : <div className="stale">Upload or paste your resume, then run the diagnostic.</div>}
    <p className="privacyNote">No invented facts. Verify evidence before adding it.</p>
  </div>
);

export const ResumeLab = forwardRef(function ResumeLab(_, fileRef) {
  const [text, setText] = useState("");
  const [ran, setRan] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [open, setOpen] = useState("evidence");
  const res = useMemo(() => (ran && text.trim() ? analyzeResume(text) : null), [ran, text]);
  const reset = (t) => { setText(t); setRan(false); setErr(""); };

  const onFile = async (file) => {
    if (!file) return;
    setBusy(true); setErr("");
    try { const t = await extractText(file, 5); setText(t); setRan(true); sessionStorage.setItem("aor_resume_text", t); }
    catch (e) { setErr(e.message || "Could not read this file."); }
    finally { setBusy(false); }
  };
  const run = () => { setRan(true); sessionStorage.setItem("aor_resume_text", text); };

  return (
    <section className="lab" id="lab">
      <div className="shell">
        <div className="sectionIntro"><p className="eyebrow">DEEP RESUME DIAGNOSTIC</p><h2>Every major signal, with the evidence behind it.</h2>
          <p>Results are rule-based and factual: the tool reports only what it can observe, then recommends the highest-impact truthful fix.</p></div>
        <div className="workbench deep">
          <div className="inputPanel">
            <div className="inputHeading"><label htmlFor="resume">Resume content</label>
              <div className="inputUtilities">
                <button type="button" onClick={() => reset(DEMO_RESUME)} data-testid="lab-demo-btn">Try demo</button>
                {text && <button type="button" onClick={() => reset("")} data-testid="lab-clear-btn">Clear</button>}
              </div></div>
            <label className={`fileDrop ${busy ? "busy" : ""}`} htmlFor="resumeFile">
              <input id="resumeFile" ref={fileRef} type="file" data-testid="lab-file-input" disabled={busy}
                accept=".txt,.docx,.pdf,text/plain,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/pdf"
                onChange={(e) => { onFile(e.target.files?.[0]); e.currentTarget.value = ""; }} />
              <b>{busy ? "Reading document…" : "Upload TXT, DOCX or PDF"}</b><span>Maximum 5 MB • processed only in your browser</span>
            </label>
            {err && <p role="alert" data-testid="lab-error" style={{ color: "#b42318", fontSize: 13, marginTop: 8 }}>{err}</p>}
            <div className="or"><span>OR PASTE THE RESUME TEXT</span></div>
            <textarea id="resume" placeholder="Paste the complete resume text here…" value={text} onChange={(e) => reset(e.target.value)} data-testid="lab-textarea" />
            <div className="inputFoot"><span data-testid="lab-word-count">{wordCount(text)} words • processed locally</span>
              <button disabled={!text.trim()} onClick={run} data-testid="lab-analyze-btn">Analyze every section →</button></div>
          </div>
          <Results res={res} />
        </div>
        {res && <div className="nuts" data-testid="lab-report">
          <div className="nutsHead"><div><p className="eyebrow">NUTS &amp; BOLTS</p><h3>10-point evidence report</h3></div>
            <div className="legend"><span className="dot criticalDot" />Critical <span className="dot highDot" />High <span className="dot passDot" />Pass</div></div>
          <div className="findingGrid">{res.findings.map((f) => <Finding key={f.id} f={f} open={open === f.id} onToggle={() => setOpen(open === f.id ? null : f.id)} />)}</div>
          <div className="disclaimer"><b>What this score is—and is not.</b><p>It measures observable resume signals, not candidate quality or hiring probability. It cannot verify whether a claim is true. Only add facts you can defend.</p></div>
        </div>}
      </div>
    </section>
  );
});
